// =============================================================================
// Mirror of packages/player/src/client/config.ts — same resolution order:
//   1. window.__bricklit.apiBase (Electron preload, edge mode)
//   2. import.meta.env.VITE_API_BASE (build-time override / LAN)
//   3. http://127.0.0.1:7890 (local-dev fallback)
//
// Phase 4 will split this further (BrowserRouter + standalone Admin SPA),
// at which point the file is imported by a browser tab as well as Electron.
// Both paths flow through the same constants.
// =============================================================================

declare global {
  interface Window {
    __bricklit?: {
      apiBase: string | null;
      appMode: string | null;
      autoUpdateFeed: string | null;
      deviceId: string | null;
      cache?: {
        resolveLocalMedia: (
          mediaId: string,
        ) => Promise<{ filePath: string; thumbnailPath: string | null } | null>;
        state: () => Promise<'online' | 'offline' | 'unknown'>;
        poke: () => Promise<void>;
      };
      pairing?: {
        status: () => Promise<{
          paired: boolean;
          deviceId: string | null;
          deviceName: string | null;
          siteLabel: string | null;
          pairedAt: string | null;
        }>;
        submit: (code: string) => Promise<{ id: string; name: string }>;
        reset: () => Promise<void>;
      };
    };
  }
}

function resolveApiBase(): string {
  if (typeof window !== 'undefined' && window.__bricklit?.apiBase) {
    return window.__bricklit.apiBase;
  }
  const fromBuild = import.meta.env.VITE_API_BASE as string | undefined;
  if (fromBuild && fromBuild.length > 0) return fromBuild;
  // Same-origin default — Vite dev proxies /api → 7890; production (Caddy)
  // serves both on the same domain. Falls back to the legacy localhost:7890
  // when running outside a browser (SSR/tests).
  if (typeof window !== 'undefined') return window.location.origin;
  return 'http://127.0.0.1:7890';
}

export const API_BASE = resolveApiBase();

export const WS_URL =
  API_BASE.replace(/^http/, (m) => (m === 'https' ? 'wss' : 'ws')) + '/ws';

export const APP_MODE: string | null =
  typeof window !== 'undefined' ? window.__bricklit?.appMode ?? null : null;

// =============================================================================
// Player 페어링 공개 주소 — 디바이스 등록 안내에 노출.
// 프로덕션(Caddy)에서는 어드민과 동일 도메인의 /player 경로로 서빙되므로
// window.location.origin 기준으로 해석. 빌드 시점 Override(VITE_PLAYER_URL)
// 또는 다른 도메인으로 분리 배포한 경우 그 값을 우선한다.
// =============================================================================
export const PLAYER_URL: string = (() => {
  const fromBuild = import.meta.env.VITE_PLAYER_URL as string | undefined;
  if (fromBuild && fromBuild.length > 0) return fromBuild;
  // 로컬 dev — admin vite(5173)에는 /player 라우트가 없어 admin SPA 폴백(#/media)으로
  // 빠진다. player는 별도 vite dev 서버(5174)이므로 그쪽을 가리킨다.
  if (import.meta.env.DEV && typeof window !== 'undefined')
    return `${window.location.protocol}//${window.location.hostname}:5174`;
  if (typeof window !== 'undefined') return `${window.location.origin}/player`;
  return 'https://nbrick-lit.ix-works.xyz/player';
})();
